import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../environments/environment';

/**
 * Vehicle make/model reference data, served by the API's cached NHTSA vPIC
 * proxy. Results are memoized per session so reopening a form doesn't refetch.
 */
@Injectable({
  providedIn: 'root'
})
export class VehicleReferenceService {
  private apiUrl = `${environment.apiUrl}/vehicle-reference`;

  private makes$: Observable<string[]> | undefined;
  private modelsCache = new Map<string, Observable<string[]>>();

  constructor(private http: HttpClient) {}

  getMakes(): Observable<string[]> {
    if (!this.makes$) {
      this.makes$ = this.http.get<string[]>(`${this.apiUrl}/makes`).pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
        catchError(error => {
          // Drop the failed observable so a later call can retry.
          this.makes$ = undefined;
          return throwError(() => error);
        })
      );
    }
    return this.makes$;
  }

  getModels(make: string, year: string): Observable<string[]> {
    const key = `${year}|${make.toLowerCase()}`;
    if (!this.modelsCache.has(key)) {
      this.modelsCache.set(
        key,
        this.http.get<string[]>(`${this.apiUrl}/models`, { params: { make, year } }).pipe(
          shareReplay({ bufferSize: 1, refCount: false }),
          catchError(error => {
            this.modelsCache.delete(key);
            return throwError(() => error);
          })
        )
      );
    }
    return this.modelsCache.get(key)!;
  }
}
